import { Storage } from '../lib/storage';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Save, Users, PhoneCall, AlertCircle, Crown, BookUser } from 'lucide-react';
import { supabase } from '../lib/supabase';
export default function SOSConfigModal({ onClose, themeClass }: { onClose: () => void, themeClass: string }) {
  const [fam1Nombre, setFam1Nombre] = useState(() => Storage.get('gravity_sos_fam1Nombre') || '');
  const [fam1Celular, setFam1Celular] = useState(() => Storage.get('gravity_sos_fam1Celular') || '');
  const [motero1Nombre, setMotero1Nombre] = useState(() => Storage.get('gravity_sos_motero1Nombre') || '');
  const [motero1Celular, setMotero1Celular] = useState(() => Storage.get('gravity_sos_motero1Celular') || '');
  const [motero2Nombre, setMotero2Nombre] = useState(() => Storage.get('gravity_sos_motero2Nombre') || '');
  const [motero2Celular, setMotero2Celular] = useState(() => Storage.get('gravity_sos_motero2Celular') || '');
  const [motero3Nombre, setMotero3Nombre] = useState(() => Storage.get('gravity_sos_motero3Nombre') || '');
  const [motero3Celular, setMotero3Celular] = useState(() => Storage.get('gravity_sos_motero3Celular') || '');
  const [guardado, setGuardado] = useState(false);
  
  const isOro = !!(Storage.get('gravity_user') && JSON.parse(Storage.get('gravity_user')!).es_oro);

  const importarDesdeAgenda = async (setter: (val: string) => void) => {
    try {
      const navSpecs = navigator as any;
      if ('contacts' in navigator && 'ContactsManager' in window) {
        const props = ['tel'];
        const opts = { multiple: false };
        const contacts = await navSpecs.contacts.select(props, opts);
        if (contacts && contacts.length > 0) {
           const phones = contacts[0].tel || [];
           if (phones.length > 0) {
              let limp = phones[0].replace(/\D/g, ''); // limpia los no numéricos
              if (limp.startsWith('57')) limp = limp.substring(2);
              setter(limp);
           } else {
              alert('Esta persona no tiene números telefónicos guardados.');
           }
        }
      } else {
         alert('Tu navegador no permite abrir la Agenda nativa. Ingresa el número manualmente.');
      }
    } catch (e: any) {
      console.warn("Agenda cancelada o fallida", e);
    }
  };

  const handleSave = async () => {
    Storage.set('gravity_sos_fam1Nombre', fam1Nombre);
    Storage.set('gravity_sos_fam1Celular', fam1Celular);
    Storage.set('gravity_sos_motero1Nombre', motero1Nombre);
    Storage.set('gravity_sos_motero1Celular', motero1Celular);
    Storage.set('gravity_sos_motero2Nombre', motero2Nombre);
    Storage.set('gravity_sos_motero2Celular', motero2Celular);
    Storage.set('gravity_sos_motero3Nombre', motero3Nombre);
    Storage.set('gravity_sos_motero3Celular', motero3Celular);
    
    // BACKUP SATELITAL (Supabase)
    const userId = Storage.get('gravity_user_id');
    if (userId) {
       try {
           const payloadSatelital = {
               fam1Nombre, fam1Celular,
               motero1Nombre, motero1Celular,
               motero2Nombre, motero2Celular,
               motero3Nombre, motero3Celular
           };
           // Importamos supabase en la parte superior del archivo (asegurarse)
           await supabase.from('usuarios').update({ contactos_sos: payloadSatelital }).eq('id', userId);
       } catch (e) {
           console.error("Error subiendo backup S.O.S a Supabase", e);
       }
    }

    setGuardado(true);
    setTimeout(() => {
      setGuardado(false);
      onClose();
    }, 1500);
  };

  return (
    <div className="absolute inset-0 z-[300] flex flex-col justify-end">
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div 
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`relative w-full ${themeClass} rounded-t-3xl p-6 shadow-[0_-10px_40px_rgba(0,168,255,0.2)] border-t border-primary-cyan/20 pb-10 max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-12 h-1 rounded-full bg-white/20 mx-auto mb-6"></div>
        <button onClick={onClose} className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors">
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-xl font-black mb-2 flex items-center gap-2 text-danger-red">
          <AlertCircle className="w-6 h-6" /> Contactos de Auxilio
        </h2>
        <p className="text-xs text-text-muted mb-6 leading-relaxed">
          Define quién recibe tu señal satelital en caso de riesgo crítico.
        </p>

        {/* Sección Familiares (WhatsApp) */}
        <div className="mb-6">
          <h3 className="text-xs font-black uppercase text-green-400 tracking-wider mb-3 flex items-center gap-2">
            <PhoneCall className="w-4 h-4" /> Familiares (WhatsApp)
          </h3>
          <p className="text-xs text-slate-400 mb-3">Recibirán enlace directo con ubicación GPS de colisión.</p>
          
          <div className="space-y-3">
            {isOro ? (
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={fam1Nombre}
                  onChange={e => setFam1Nombre(e.target.value)}
                  className="w-1/2 px-4 py-3 rounded-xl border border-white/20 bg-slate-800/90 text-white outline-none focus:border-green-400 focus:bg-slate-800 text-[13px] font-bold placeholder:text-slate-500 uppercase transition-colors" 
                  placeholder="👤 Nombre Parentesco" 
                />
                <div className="w-1/2 relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400">+57</div>
                  <input 
                    type="tel" 
                    value={fam1Celular}
                    onChange={e => setFam1Celular(e.target.value)}
                    className="w-full pl-[42px] pr-10 py-3 rounded-xl border border-white/20 bg-slate-800/90 text-white outline-none focus:border-green-400 focus:bg-slate-800 text-[13px] font-bold placeholder:text-slate-500 transition-colors" 
                    placeholder="300 000 0000" 
                  />
                  <button onClick={() => importarDesdeAgenda(setFam1Celular)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors" title="Buscar en Agenda">
                     <BookUser className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="relative cursor-not-allowed">
                <input 
                  disabled
                  type="tel" 
                  className="w-full px-4 py-2.5 rounded-xl border border-white/5 bg-black/50 text-slate-500 outline-none text-sm font-bold placeholder:text-slate-600 opacity-60 pointer-events-none" 
                  placeholder="Familiar / WhatsApp (Exclusivo Libre ORO)" 
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Crown className="w-4 h-4 text-yellow-500 opacity-50" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sección Moteros (In-App) */}
        <div className="mb-8">
          <h3 className="text-xs font-black uppercase text-primary-cyan tracking-wider mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" /> Gravity Bike de Rescate (In-App)
          </h3>
          <p className="text-xs text-slate-400 mb-3">Recibirán alarma instantánea dentro de GRAVITY BIKE.</p>
          
          <div className="space-y-3">
            <div className="flex gap-2">
              <input 
                type="text" 
                value={motero1Nombre}
                onChange={e => setMotero1Nombre(e.target.value)}
                className="w-1/2 px-4 py-3 rounded-xl border border-white/20 bg-slate-800/90 text-white outline-none focus:border-primary-cyan focus:bg-slate-800 text-[13px] font-bold placeholder:text-slate-500 uppercase transition-colors" 
                placeholder="👤 Alias Gravity Bike" 
              />
              <div className="w-1/2 relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400">+57</div>
                <input 
                  type="tel" 
                  value={motero1Celular}
                  onChange={e => setMotero1Celular(e.target.value)}
                  className="w-full pl-[42px] pr-10 py-3 rounded-xl border border-white/20 bg-slate-800/90 text-white outline-none focus:border-primary-cyan focus:bg-slate-800 text-[13px] font-bold placeholder:text-slate-500 transition-colors" 
                  placeholder="300 000 0000" 
                />
                <button onClick={() => importarDesdeAgenda(setMotero1Celular)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors" title="Buscar en Agenda">
                   <BookUser className="w-4 h-4" />
                </button>
              </div>
            </div>
            {isOro ? (
              <>
                <div className="flex gap-2 transition-all">
                  <input 
                    type="text" 
                    value={motero2Nombre}
                    onChange={e => setMotero2Nombre(e.target.value)}
                    className="w-1/2 px-4 py-3 rounded-xl border border-white/20 bg-slate-800/90 text-white outline-none focus:border-primary-cyan focus:bg-slate-800 text-[13px] font-bold placeholder:text-slate-500 uppercase transition-colors" 
                    placeholder="👤 Alias Gravity Bike 2" 
                  />
                  <div className="w-1/2 relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400">+57</div>
                    <input 
                      type="tel" 
                      value={motero2Celular}
                      onChange={e => setMotero2Celular(e.target.value)}
                      className="w-full pl-[42px] pr-10 py-3 rounded-xl border border-white/20 bg-slate-800/90 text-white outline-none focus:border-primary-cyan focus:bg-slate-800 text-[13px] font-bold placeholder:text-slate-500 transition-colors" 
                      placeholder="300 000 0000" 
                    />
                    <button onClick={() => importarDesdeAgenda(setMotero2Celular)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors" title="Buscar en Agenda">
                       <BookUser className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="flex gap-2 transition-all">
                  <input 
                    type="text" 
                    value={motero3Nombre}
                    onChange={e => setMotero3Nombre(e.target.value)}
                    className="w-1/2 px-4 py-3 rounded-xl border border-white/20 bg-slate-800/90 text-white outline-none focus:border-primary-cyan focus:bg-slate-800 text-[13px] font-bold placeholder:text-slate-500 uppercase transition-colors" 
                    placeholder="👤 Alias Gravity Bike 3" 
                  />
                  <div className="w-1/2 relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400">+57</div>
                    <input 
                      type="tel" 
                      value={motero3Celular}
                      onChange={e => setMotero3Celular(e.target.value)}
                      className="w-full pl-[42px] pr-10 py-3 rounded-xl border border-white/20 bg-slate-800/90 text-white outline-none focus:border-primary-cyan focus:bg-slate-800 text-[13px] font-bold placeholder:text-slate-500 transition-colors" 
                      placeholder="300 000 0000" 
                    />
                    <button onClick={() => importarDesdeAgenda(setMotero3Celular)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors" title="Buscar en Agenda">
                       <BookUser className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="relative cursor-not-allowed">
                  <input 
                    disabled
                    type="tel" 
                    className="w-full px-4 py-2.5 rounded-xl border border-white/5 bg-black/50 text-slate-500 outline-none text-sm font-bold placeholder:text-slate-600 pointer-events-none opacity-60" 
                    placeholder="Gravity Bike 2 (Exclusivo ORO / PRO)" 
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Crown className="w-4 h-4 text-yellow-500 opacity-50" />
                  </div>
                </div>
                <div className="relative cursor-not-allowed">
                  <input 
                    disabled
                    type="tel" 
                    className="w-full px-4 py-2.5 rounded-xl border border-white/5 bg-black/50 text-slate-500 outline-none text-sm font-bold placeholder:text-slate-600 pointer-events-none opacity-60" 
                    placeholder="Gravity Bike 3 (Exclusivo ORO / PRO)" 
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Crown className="w-4 h-4 text-yellow-500 opacity-50" />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <button 
          onClick={handleSave}
          disabled={guardado || (!fam1Celular && !motero1Celular)}
          className="w-full py-4 bg-danger-red rounded-xl font-black text-[12px] uppercase tracking-widest text-white hover:bg-red-600 active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:bg-slate-700 shadow-[0_0_20px_rgba(239,68,68,0.4)]"
        >
          {guardado ? '¡BLINDADOS! ✔️' : <><Save className="w-5 h-5 mb-0.5" /> GUARDAR RESCATISTAS</>}
        </button>

      </motion.div>
    </div>
  );
}
